import { useEffect, useState } from "react";
import { CalendarClock, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle, Empty, Stat } from "@/components/Common";
import { HorarioSemanal, DIAS, colorDe } from "@/components/HorarioSemanal";

const minutos = (h) => Number(h.split(":")[0]) * 60 + Number(h.split(":")[1]);

export default function HorarioClases() {
  const { activeId, activeStudent } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    setData(null);
    api.get(`/estudiantes/${activeId}/horario`)
      .then((r) => setData(r.data))
      .catch(() => setData({ bloques: [], grado: activeStudent?.grado }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId) return <Empty>No hay estudiantes asociados.</Empty>;
  if (!data) return <Loading />;

  const bloques = data.bloques || [];
  const hoy = (new Date().getDay() + 6) % 7;
  const deHoy = bloques.filter((b) => b.dia === hoy);
  const horas = bloques.reduce((t, b) => t + (minutos(b.hora_fin) - minutos(b.hora_inicio)) / 60, 0);
  const conSubtitulo = bloques.map((b) => ({
    ...b, subtitulo: [b.docente_nombre, b.aula].filter(Boolean).join(" · "),
  }));
  const usaSabado = bloques.some((b) => b.dia === 5);

  return (
    <div data-testid="portal-horario">
      <PageTitle title="Horario de clases"
                 subtitle={`Semana de clases de ${data.estudiante || activeStudent?.nombre || ""} · ${data.grado || ""}`} />

      <div className="grid gap-4 sm:grid-cols-3" data-testid="horario-portal-stats">
        <Stat label="Clases hoy" value={String(deHoy.length)} icon={CalendarClock} />
        <Stat label="Horas semanales" value={`${horas.toFixed(1)} h`} />
        <Stat label="Grado" value={data.grado || "—"} />
      </div>

      {bloques.length === 0 ? (
        <div className="mt-8">
          <Empty testId="horario-portal-vacio">
            El colegio todavía no ha publicado el horario de {data.grado || "este grado"}.
          </Empty>
        </div>
      ) : (
        <>
          <section className="mt-8">
            <h2 className="font-display text-lg font-semibold">Hoy · {DIAS[hoy] || "Domingo"}</h2>
            {deHoy.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">Hoy no hay clases programadas.</p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="clases-hoy-portal">
                {deHoy.map((b) => (
                  <article key={b.id} className={`lift rounded-lg border p-5 ${colorDe(b.materia_nombre)}`}>
                    <p className="font-mono text-xs opacity-80">{b.hora_inicio} – {b.hora_fin}</p>
                    <h3 className="mt-1 font-display text-base font-bold">{b.materia_nombre}</h3>
                    {b.docente_nombre && <p className="mt-1 text-xs opacity-90">{b.docente_nombre}</p>}
                    {b.aula && (
                      <p className="mt-2 flex items-center gap-1 text-xs opacity-90">
                        <MapPin className="h-3 w-3" /> {b.aula}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="mt-10">
            <h2 className="font-display text-lg font-semibold">Semana completa</h2>
            <div className="mt-4">
              <HorarioSemanal bloques={conSubtitulo} dias={usaSabado ? 6 : 5} diaResaltado={hoy} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
